import React, { useState, useMemo } from 'react';
import TimeField from './TimeField';

const Step3_RollUpConfig = ({ data, updateData, isLocked }) => {
    const [config, setConfig] = useState(() => {
        const baseConfig = data.tee_sheet_config || {
            start_time: '',
            interval: '10',
            num_groups: '18',
            slots_per_group: 4,
            double_bank: false
        };
        // Ensure is_shotgun is NOT in the internal config object
        const { is_shotgun, ...rest } = baseConfig;
        return rest;
    });

    const [isShotgunMode, setIsShotgunMode] = useState(data.is_shotgun || false);

    const handleChange = (e) => {
        if (isLocked) return;
        const { name, value, type, checked } = e.target;
        const val = type === 'checkbox' ? checked : value;
        const newConfig = { ...config, [name]: val };
        setConfig(newConfig);
        updateData({ tee_sheet_config: newConfig });
    };

    const setFormat = (isShotgun) => {
        if (isLocked) return;
        setIsShotgunMode(isShotgun);
        updateData({ is_shotgun: isShotgun, tee_sheet_config: config });
    };

    const isConfigValid = useMemo(() => {
        const groups = Number(config.num_groups);
        const startTimeOk = config.start_time !== '';
        const groupsOk = !isNaN(groups) && groups > 0;
        
        if (isShotgunMode) {
            return startTimeOk && groupsOk;
        } else {
            const interval = Number(config.interval);
            return startTimeOk && !isNaN(interval) && interval > 0 && groupsOk;
        }
    }, [config, isShotgunMode]);

    const displaySlots = useMemo(() => {
        if (!isConfigValid) return [];
        const slots = [];
        
        if (isShotgunMode) {
            const groups = Number(config.num_groups);
            const doubleBank = config.double_bank;
            
            for (let i = 0; i < groups; i++) {
                const holeNum = i + 1;
                slots.push({ label: `Hole ${holeNum}`, short: `H${holeNum}`, time: config.start_time });
                if (doubleBank) {
                    slots.push({ label: `Hole ${holeNum} (B)`, short: `H${holeNum}B`, time: config.start_time });
                }
            }
        } else {
            try {
                const [hours, minutes] = config.start_time.split(':').map(Number);
                let current = new Date(2000, 0, 1, hours, minutes);
                const interval = Number(config.interval);
                const groups = Number(config.num_groups);
                
                for (let i = 0; i < groups; i++) {
                    const time = current.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
                    slots.push({ label: `Group ${i + 1}`, short: time, time });
                    current = new Date(current.getTime() + interval * 60000);
                }
            } catch (e) {
                return [];
            }
        }
        return slots;
    }, [config, isConfigValid, isShotgunMode]);

    const totalSlotsCount = useMemo(() => {
        const perGroup = Number(config.slots_per_group) || 0;
        return displaySlots.length * perGroup;
    }, [displaySlots, config.slots_per_group]);

    return (
        <div className="wizard-step sheetcfg">
            {isLocked && (
                <div className="sheetcfg-locked">🔒 Tournament started — event details are locked</div>
            )}

            {/* How the round starts, and the settings for that choice, in ONE
                card. They were two, each with its own title, subtitle and 20px
                of padding — and the second card's title ("Shotgun Settings")
                only ever restated the pill already lit above it. */}
            <div className="config-card sheetcfg-card">
                <div
                    className="segmented-control"
                    style={{ opacity: isLocked ? 0.7 : 1, pointerEvents: isLocked ? 'none' : 'auto' }}
                >
                    <button
                        type="button"
                        className={`segmented-control__item ${!isShotgunMode ? 'active' : ''}`}
                        onClick={() => setFormat(false)}
                        disabled={isLocked}
                    >
                        Tee Times
                    </button>
                    <button
                        type="button"
                        className={`segmented-control__item ${isShotgunMode ? 'active' : ''}`}
                        onClick={() => setFormat(true)}
                        disabled={isLocked}
                    >
                        Shotgun Start
                    </button>
                </div>

                {/* Two columns of stacked fields — four short answers laid out
                    2×2 in the height four full-width label/value rows took. Both
                    modes have exactly four, so neither leaves a hole in the
                    grid. */}
                <div className="sheetcfg-grid">
                    <div className="sheetcfg-field">
                        <span className="sheetcfg-field__label">Start time</span>
                        <div className="sheetcfg-field__control">
                            <TimeField
                                name="start_time"
                                value={config.start_time}
                                onChange={handleChange}
                                disabled={isLocked}
                                ariaLabel="Start time"
                                title="Select a start time"
                            />
                        </div>
                    </div>

                    {!isShotgunMode ? (
                        <>
                            <div className="sheetcfg-field">
                                <span className="sheetcfg-field__label">Interval</span>
                                <div className="sheetcfg-field__control">
                                    <input
                                        type="number"
                                        name="interval"
                                        className="engine-input engine-input--number"
                                        placeholder="10"
                                        value={config.interval}
                                        onChange={handleChange}
                                        disabled={isLocked}
                                    />
                                    <span className="engine-unit">mins</span>
                                </div>
                            </div>
                            <div className="sheetcfg-field">
                                <span className="sheetcfg-field__label">Total groups</span>
                                <div className="sheetcfg-field__control">
                                    <input
                                        type="number"
                                        name="num_groups"
                                        className="engine-input engine-input--number"
                                        placeholder="e.g. 10"
                                        value={config.num_groups}
                                        onChange={handleChange}
                                        disabled={isLocked}
                                    />
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="sheetcfg-field">
                            <span className="sheetcfg-field__label">Starting holes</span>
                            <div className="sheetcfg-field__control">
                                <input
                                    type="number"
                                    name="num_groups"
                                    className="engine-input engine-input--number"
                                    placeholder="18"
                                    value={config.num_groups}
                                    onChange={handleChange}
                                    disabled={isLocked}
                                />
                                <span className="engine-unit">holes</span>
                            </div>
                        </div>
                    )}

                    <div className="sheetcfg-field">
                        <span className="sheetcfg-field__label">Group size</span>
                        <div className="sheetcfg-field__control">
                            <select
                                name="slots_per_group"
                                className="engine-input engine-input--select"
                                value={config.slots_per_group}
                                onChange={handleChange}
                                disabled={isLocked}
                            >
                                <option value="2">2-ball</option>
                                <option value="3">3-ball</option>
                                <option value="4">4-ball</option>
                            </select>
                        </div>
                    </div>

                    {isShotgunMode && (
                        <div className="sheetcfg-field">
                            <span className="sheetcfg-field__label">Double groups</span>
                            <div className="sheetcfg-field__control">
                                <label className="switch">
                                    <input
                                        type="checkbox"
                                        name="double_bank"
                                        checked={config.double_bank}
                                        onChange={handleChange}
                                        disabled={isLocked}
                                    />
                                    <span className="slider round"></span>
                                </label>
                                <span className="sheetcfg-field__hint">2 per hole</span>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* The sheet as chips, not rows. Every row said the same thing —
                a label and four identical dots — so eighteen of them cost 600px
                to convey what the summary line says in one. Chips wrap, so the
                whole draw is visible at a glance and the card stops growing a
                row at a time. */}
            <div className="config-card sheetcfg-card sheetcfg-preview">
                <div className="sheetcfg-preview__head">
                    <span className="config-card__title">
                        {isShotgunMode ? 'Shotgun sheet' : 'Tee sheet'}
                    </span>
                    {isConfigValid && (
                        <span className="sheetcfg-preview__summary">
                            <strong>{displaySlots.length}</strong> groups
                            {' · '}
                            <strong>{totalSlotsCount}</strong> slots
                            {!isShotgunMode && (
                                <>{' · ends '}<strong>{displaySlots[displaySlots.length - 1].time}</strong></>
                            )}
                        </span>
                    )}
                </div>

                {displaySlots.length > 0 ? (
                    <div className="sheetcfg-preview__chips">
                        {displaySlots.map((slot, i) => (
                            <span key={i} className="sheetcfg-chip" title={slot.label}>{slot.short}</span>
                        ))}
                    </div>
                ) : (
                    <p className="sheetcfg-preview__empty">
                        Complete the details above to see the sheet
                    </p>
                )}
            </div>
        </div>
    );
};

export default Step3_RollUpConfig;